/**
 * Enumerations shared by the API and the web client.
 *
 * Every one of these is also a Postgres CHECK constraint or an enum-backed text column
 * (`apps/api/src/db/schema.ts`). When a member is added here it must be added there in
 * the same commit — the database is the authority, this file is how both sides agree on
 * what it will accept.
 */

/* -------------------------------------------------------------------------- */
/* Content lifecycle                                                           */
/* -------------------------------------------------------------------------- */

/**
 * One lifecycle for every piece of content — pages, projects, posts, services.
 *
 * DRAFT is invisible to the public API entirely (not 403, not a stub: absent), so an
 * unfinished project cannot leak through a guessed slug. ARCHIVED is the reversible
 * alternative to deletion and is what the back office should reach for by default:
 * every public list filters on PUBLISHED, so archiving takes something off the site
 * while leaving its history, its media and its inbound links intact.
 */
export const CONTENT_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export type ContentStatus = (typeof CONTENT_STATUSES)[number];

/* -------------------------------------------------------------------------- */
/* Store                                                                       */
/* -------------------------------------------------------------------------- */

export const PRODUCT_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

/**
 * Stock movement kinds. `stock_movements` is an append-only ledger and
 * `inventory.on_hand` is its running total — never edited directly. See §5.
 *
 * Signs are fixed by `MOVEMENT_SIGN` rather than left to the caller, because the one
 * bug this ledger cannot survive is a receipt booked as a negative.
 */
export const MOVEMENT_TYPES = [
  'GRN',        // goods received from a supplier
  'SALE',       // an order was fulfilled
  'RETURN',     // a customer sent it back, saleable again
  'ADJUSTMENT', // a stock count disagreed with the ledger; signed, needs a reason
  'DAMAGE',     // written off, not saleable
  'RESERVE',    // committed to an unfulfilled order — moves `reserved`, not `on_hand`
  'RELEASE',    // that commitment ended (order cancelled or fulfilled)
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

/**
 * Which column a movement moves, and in which direction.
 *
 * `ADJUSTMENT` is the only signed type: its quantity carries its own sign because a
 * count can go either way. Every other type has one direction by definition, and
 * letting a caller choose is how a receipt becomes a write-off.
 */
export const MOVEMENT_EFFECT: Record<
  MovementType,
  { column: 'onHand' | 'reserved'; sign: 1 | -1 | 'signed'; label: string }
> = {
  GRN:        { column: 'onHand',   sign: 1,        label: 'Goods received' },
  SALE:       { column: 'onHand',   sign: -1,       label: 'Sold' },
  RETURN:     { column: 'onHand',   sign: 1,        label: 'Customer return' },
  ADJUSTMENT: { column: 'onHand',   sign: 'signed', label: 'Stock adjustment' },
  DAMAGE:     { column: 'onHand',   sign: -1,       label: 'Damaged / written off' },
  RESERVE:    { column: 'reserved', sign: 1,        label: 'Reserved for an order' },
  RELEASE:    { column: 'reserved', sign: -1,       label: 'Reservation released' },
};

/** Movement kinds a human may post by hand. The rest are written by the order engine. */
export const MANUAL_MOVEMENT_TYPES = ['GRN', 'ADJUSTMENT', 'DAMAGE', 'RETURN'] as const;
export type ManualMovementType = (typeof MANUAL_MOVEMENT_TYPES)[number];

/**
 * Order lifecycle. Forward-only except for the two terminal exits, enforced by
 * `ORDER_TRANSITIONS` server-side — a status is never taken from the client as-is.
 */
export const ORDER_STATUSES = [
  'PENDING',   // placed, stock reserved, nobody has looked at it yet
  'CONFIRMED', // accepted by the office
  'PACKED',
  'SHIPPED',
  'DELIVERED', // terminal, success — reservation converted to a SALE movement
  'CANCELLED', // terminal — reservation released
  'REFUNDED',  // terminal, after DELIVERED — stock returned
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * The only status changes the API will make. An order cannot skip backwards into a
 * state whose stock side-effects have already been applied, which is what keeps the
 * ledger and the order table from disagreeing.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  PENDING:   ['CONFIRMED', 'CANCELLED'],
  CONFIRMED: ['PACKED', 'CANCELLED'],
  PACKED:    ['SHIPPED', 'CANCELLED'],
  SHIPPED:   ['DELIVERED', 'CANCELLED'],
  DELIVERED: ['REFUNDED'],
  CANCELLED: [],
  REFUNDED:  [],
};

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PACKED: 'Packed',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
};

/**
 * How the customer intends to pay. This deployment takes no card details and settles
 * no money online — see TECHNICAL_DOCUMENT.md §6 invariant 7. An order is a firm
 * request that reserves stock; payment is arranged off-site.
 */
export const PAYMENT_METHODS = ['BANK_TRANSFER', 'CASH_ON_DELIVERY', 'INVOICE_ME'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  BANK_TRANSFER: 'Bank transfer',
  CASH_ON_DELIVERY: 'Cash on delivery',
  INVOICE_ME: 'Invoice my company',
};

export const FULFILMENT_METHODS = ['DELIVERY', 'PICKUP'] as const;
export type FulfilmentMethod = (typeof FULFILMENT_METHODS)[number];

/* -------------------------------------------------------------------------- */
/* Enquiries                                                                   */
/* -------------------------------------------------------------------------- */

export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUOTED', 'WON', 'LOST'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Which form the enquiry arrived through. Drives nothing but reporting. */
export const LEAD_SOURCES = ['CONTACT', 'QUOTE', 'PROJECT_ENQUIRY', 'PRODUCT_ENQUIRY'] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

/* -------------------------------------------------------------------------- */
/* Page composition                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The section catalogue — the whole of "highly customizable".
 *
 * A page is an ordered list of sections; each section is a `type` from this list plus a
 * JSON `payload` validated by the matching schema in `schemas.ts`. Adding a section
 * type is: a member here, a payload schema there, and one React component registered in
 * `apps/web/src/sections/registry.tsx`. Nothing else in the system knows the list.
 *
 * Sections that read from a table (`featured_project`, `product_grid`, `team`, …) hold
 * only *selection* in their payload — how many, which category, which order. The
 * records themselves stay in their own tables, so editing a project does not mean
 * finding every page that shows it.
 */
export const SECTION_TYPES = [
  'hero',
  'marquee',
  'trust_bar',
  'capabilities',
  'configurator',
  'practice',
  'deployment_record',
  'contact_split',
  'services_grid',
  'featured_project',
  'project_grid',
  'product_grid',
  'stats',
  'testimonials',
  'team',
  'milestones',
  'image_text',
  'rich_text',
  'faq',
  'cta_split',
  'cta_banner',
  'contact_form',
  'newsletter',
  'logo_wall',
] as const;
export type SectionType = (typeof SECTION_TYPES)[number];

export const SECTION_LABEL: Record<SectionType, string> = {
  hero: 'Hero',
  marquee: 'Scrolling marquee',
  trust_bar: 'Trusted-by bar',
  capabilities: 'Capabilities accordion',
  configurator: 'Counter configurator',
  practice: 'How we work',
  deployment_record: 'Deployment record',
  contact_split: 'Contact — form and details',
  services_grid: 'Services grid',
  featured_project: 'Featured project',
  project_grid: 'Project grid',
  product_grid: 'Product grid',
  stats: 'Statistics row',
  testimonials: 'Testimonials',
  team: 'Team',
  milestones: 'Milestones timeline',
  image_text: 'Image + text',
  rich_text: 'Rich text',
  faq: 'FAQ',
  cta_split: 'Split call to action',
  cta_banner: 'Call-to-action banner',
  contact_form: 'Contact form',
  newsletter: 'Newsletter sign-up',
  logo_wall: 'Logo wall',
};

/** One-line hints shown in the "add a section" picker. */
export const SECTION_HINT: Record<SectionType, string> = {
  hero: 'Headline, supporting text and up to two buttons, beside a picture or the animated counter.',
  marquee: 'A single line of short phrases scrolling sideways.',
  trust_bar: 'A quiet strip of customer names under the hero.',
  capabilities: 'Numbered rows that open one at a time to reveal detail.',
  configurator: 'The live counter builder — choices on the left, a running bill of materials on the right.',
  practice: 'A dark panel: how the work is done, as numbered cards, with the headline figures under it.',
  deployment_record: 'One installation told as a timeline, with a photograph and the client’s own words.',
  contact_split: 'The enquiry form beside the office address, telephone and email.',
  services_grid: 'Cards drawn from Services. Choose how many.',
  featured_project: 'The one project flagged as featured, shown large.',
  project_grid: 'Cards drawn from Projects. Choose how many.',
  product_grid: 'Cards drawn from the store. Filter by category and choose how many.',
  stats: 'A row of large figures with captions.',
  testimonials: 'Customer quotes drawn from Testimonials.',
  team: 'People drawn from Team.',
  milestones: 'A year-by-year timeline drawn from Milestones.',
  image_text: 'A picture beside a block of text; the side is yours to pick.',
  rich_text: 'A heading and body copy on its own.',
  faq: 'Questions that expand to answers.',
  cta_split: 'Two panels side by side, each with its own button.',
  cta_banner: 'One full-width band with a heading and a button.',
  contact_form: 'The enquiry form. Submissions land in Enquiries.',
  newsletter: 'Email capture. Addresses land in Subscribers.',
  logo_wall: 'A grid of customer or partner logos.',
};

/* -------------------------------------------------------------------------- */
/* Navigation                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Where a navigation item appears. The footer's three link columns are separate
 * locations rather than one list with a column number, so re-ordering a column cannot
 * push an item into the one beside it.
 */
export const NAV_LOCATIONS = ['HEADER', 'FOOTER_PRIMARY', 'FOOTER_SECONDARY', 'LEGAL'] as const;
export type NavLocation = (typeof NAV_LOCATIONS)[number];

export const NAV_LOCATION_LABEL: Record<NavLocation, string> = {
  HEADER: 'Header',
  FOOTER_PRIMARY: 'Footer — first column',
  FOOTER_SECONDARY: 'Footer — second column',
  LEGAL: 'Footer — legal strip',
};

/* -------------------------------------------------------------------------- */
/* Email                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What a message in the outbox is FOR.
 *
 * Recorded per message rather than inferred from the subject, because the subject is
 * editable copy and this is what the log is filtered and reasoned about by.
 */
export const EMAIL_KINDS = [
  /** To the customer, the moment an order is placed. */
  'ORDER_CONFIRMATION',
  /** To the customer, when an order moves to a new status. */
  'ORDER_STATUS',
  /** To the office, when an order is placed. */
  'ORDER_ALERT',
  /** To the office, when a contact or quote form is submitted. */
  'LEAD_ALERT',
  /** Sent by hand from the settings screen, to prove the configuration works. */
  'TEST',
] as const;
export type EmailKind = (typeof EMAIL_KINDS)[number];

/**
 * `QUEUED` is the state every message starts in, including the ones that will be sent
 * a millisecond later. Nothing is ever handed to the transport without a row existing
 * first: a crash between "sent" and "recorded" must leave evidence that something was
 * attempted, and the only way to guarantee that is to write before sending.
 *
 * `FAILED` is retryable — the flusher picks it up again until `EMAIL_MAX_ATTEMPTS`.
 * `ABANDONED` is terminal and means a human has to look.
 */
export const EMAIL_STATUSES = ['QUEUED', 'SENT', 'FAILED', 'ABANDONED'] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

/** After this many failures a message stops being retried and waits for a human. */
export const EMAIL_MAX_ATTEMPTS = 5;

/**
 * Backoff between attempts, in minutes, indexed by attempts already made.
 *
 * Deliberately not exponential past an hour. A mailbox that has been refusing for an
 * hour is having an outage or a credential problem, and neither is fixed by waiting
 * eight hours to look again — but retrying every minute for a day is how an account
 * gets rate-limited by its own provider.
 */
export const EMAIL_RETRY_MINUTES = [1, 5, 20, 60, 60] as const;

/* -------------------------------------------------------------------------- */
/* Settings                                                                    */
/* -------------------------------------------------------------------------- */

/** Settings are grouped only for the screen; the store itself is flat key → JSON. */
export const SETTING_GROUPS = [
  'brand',
  'contact',
  'social',
  'seo',
  'store',
  'appearance',
  'email',
  'integrations',
] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

/* -------------------------------------------------------------------------- */
/* Money                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Money crosses the wire as a decimal STRING and is stored as `NUMERIC(14,2)`.
 *
 * It is never a JavaScript number in transit. `pg` returns NUMERIC as a string for
 * exactly this reason, and the moment a total is parsed to a float to add it up, the
 * cents stop being reliable — a 20-line order is enough to show it.
 */
export const MONEY_SCALE = 2;
export const DEFAULT_CURRENCY = 'LKR';
